import {
  StorageSerializers,
  useLocalStorage,
  usePreferredDark,
} from '@vueuse/core'

const guestDarkMode = useLocalStorage<boolean | null>('guestDarkMode', null, {
  serializer: StorageSerializers.boolean,
})
const systemDarkMode = usePreferredDark()

export function useAppearance() {
  const { account } = useAuth()
  const { $api } = useNuxtApp()
  const toast = useToast()
  const savingAppearance = useState('savingAppearance', () => false)
  const darkMode = computed(
    () =>
      (account.value ? account.value.dark_mode : guestDarkMode.value) ??
      systemDarkMode.value
  )

  async function toggleDarkMode() {
    if (savingAppearance.value) return
    const enabled = !darkMode.value
    const user = account.value
    if (!user) {
      guestDarkMode.value = enabled
      return
    }
    const previous = user.dark_mode
    user.dark_mode = enabled
    savingAppearance.value = true
    try {
      const saved = await $api<components['schemas']['Account']>('/account/', {
        method: 'PATCH',
        body: { dark_mode: enabled },
        keepalive: true,
      })
      if (saved.dark_mode !== enabled)
        throw new Error('Appearance was not saved')
    } catch {
      if (account.value?.id === user.id) {
        account.value.dark_mode = previous
        toast.add({
          severity: 'error',
          summary: 'Could not save appearance',
          detail: 'Please try again.',
          life: 4500,
        })
      }
    } finally {
      savingAppearance.value = false
    }
  }

  return { darkMode, savingAppearance, toggleDarkMode }
}

export function useAppearanceClass() {
  const { darkMode } = useAppearance()
  useHead({
    htmlAttrs: { class: computed(() => (darkMode.value ? 'app-dark' : '')) },
  })
}
